import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/authStore'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedCategoryName } from '@/lib/localization-helpers'
import { useQuery } from '@tanstack/react-query'
import { Main } from '@/components/layout/main'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FileText, Plus } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import type { DocumentTemplate, DocumentTemplateList } from '@/types/document-template'
import api_base from '@/api/base'
import { DataTable } from '@/components/table'
import { delete_document_template } from '@/api/document-templates'
import { useToast } from '@/hooks/use-toast'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconEdit, IconEye, IconTrash } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/_authenticated/super-admin-document-templates')({
  beforeLoad: async () => {
    const auth = useAuthStore.getState().auth;

    // Check if user is authenticated
    if (!auth.accessToken || !auth.user) {
      throw redirect({
        to: '/auth/login',
      });
    }

    // Check if user has super admin role
    // Check if user has super admin role
    if (auth.user.role !== 'Super Admin' && auth.user.role_name !== 'Super Admin') {
      throw redirect({
        to: '/dashboard',
      });
    }
  },
  component: Page,
})

// Super admin templates API
const list_super_admin_templates = async (
  params: { q?: string | null; page_size?: number; page?: number; category?: string | null } = {}
): Promise<DocumentTemplateList> => {
  try {
    const response = await api_base.get('/api/v1/super-admin/document-templates/', { params })
    return response.data
  } catch (error: any) {
    throw error?.response?.data || error
  }
}

function Page() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [search, setSearch] = useState<string>('')
  const [categories, setCategories] = useState<Array<{ id: number; name: string; name_ar: string }>>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplate | null>(null)
  const [params, setParams] = useState<{ page: number; page_size: number; q: string | null; category: string | null }>({
    page: 0,
    page_size: 10,
    q: null,
    category: null
  })

  // Fetch categories
  useEffect(() => {
    api_base
      .get('/api/v1/super-admin/document-template-categories/')
      .then((res) => setCategories(res.data.results || res.data))
      .catch((err) => console.error('Failed to fetch categories:', err))
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams(prev => ({ ...prev, q: search || null, page: 0 }))
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  const { error, data } = useQuery({
    queryKey: ['super-admin-templates', params],
    queryFn: () => list_super_admin_templates(params),
  })

  // Handle row click to navigate to template details
  const handleRowClick = (row: DocumentTemplate) => {
    navigate({ to: `/super-admin-document-template/${row.slug}` })
  }

  // Handle delete template
  const handleDelete = async () => {
    if (!templateToDelete) return

    try {
      await delete_document_template(templateToDelete.slug)
      toast({
        title: t('success'),
        description: t('template_deleted_successfully') || 'Template deleted successfully',
      })
      setDeleteDialogOpen(false)
      setTemplateToDelete(null)
      // Refetch the data
      window.location.reload()
    } catch (error: any) {
      toast({
        title: t('error'),
        description: error?.message || t('error_deleting_template') || 'Failed to delete template',
        variant: 'destructive',
      })
    }
  }

  const columns: ColumnDef<DocumentTemplate>[] = [
    {
      accessorKey: 'title',
      header: t('title'),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <p className="font-medium">{row.original.title}</p>
          <p className="text-xs text-muted-foreground">{row.original.slug}</p>
        </div>
      ),
    },
    {
      accessorKey: 'version',
      header: t('version'),
      cell: ({ row }) => <span className="text-sm">v{row.original.version}</span>,
    },
    {
      accessorKey: 'category',
      header: t('category'),
      cell: ({ row }) => {
        const category = row.original.category
        if (!category) return <span className="text-sm text-muted-foreground">-</span>
        if (typeof category === 'string') return <span className="text-sm">{category}</span>
        return <span className="text-sm">{getLocalizedCategoryName(category, i18n.language)}</span>
      },
    },
    {
      accessorKey: 'engine',
      header: t('engine'),
      cell: ({ row }) => (
        <Badge variant={row.original.engine === 'word' ? 'default' : 'secondary'}>
          {row.original.engine?.toUpperCase() || 'HTML'}
        </Badge>
      ),
    },
    {
      accessorKey: 'is_active',
      header: t('status'),
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'default' : 'secondary'}>
          {row.original.is_active ? t('active') : t('inactive')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
              onClick={(e) => e.stopPropagation()}
            >
              <DotsHorizontalIcon className='h-4 w-4' />
              <span className='sr-only'>{t('open_menu')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-[160px]'>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                navigate({ to: `/super-admin-document-template/${row.original.slug}` })
              }}
            >
              {t('edit')}
              <DropdownMenuShortcut>
                <IconEdit size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                if (row.original.preview_url) {
                  window.open(row.original.preview_url, '_blank')
                }
              }}
            >
              {t('preview')}
              <DropdownMenuShortcut>
                <IconEye size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setTemplateToDelete(row.original)
                setDeleteDialogOpen(true)
              }}
              className='!text-red-500'
            >
              {t('delete')}
              <DropdownMenuShortcut>
                <IconTrash size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
    // per-row variables & actions removed to match cases UI (actions handled via dialogs/toolbar)
  ]

  return (
    <>


      <Main>
        <div className='mb-6 flex items-center justify-between'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight flex items-center gap-2'>
              <FileText className='w-6 h-6' />
              {t('document_templates') || 'Document Templates'}
            </h2>
            <p className='text-muted-foreground'>{t('manage_document_templates') || 'Create and manage document templates for cabinets'}</p>
          </div>
          <Button
            className='gap-2'
            onClick={() => navigate({ to: '/super-admin-document-template-create' })}
          >
            <Plus className='w-4 h-4' />
            {t('create_template') || 'Create Template'}
          </Button>
        </div>

        <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4'>
          <div className='flex-1'>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search_templates') || 'Search templates...'}
            />
          </div>
          <div className='w-full sm:w-48'>
            <Select
              value={selectedCategory}
              onValueChange={(value) => {
                setSelectedCategory(value)
                setParams(prev => ({ ...prev, category: value === 'all' ? null : value, page: 0 }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('all_categories') || 'All Categories'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_categories') || 'All Categories'}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={String(category.id)}>
                    {category.name_ar || category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error ? (
          <Card>
            <CardContent className='p-8 text-center text-destructive'>
              {t('error_loading') || 'Error loading templates'}
            </CardContent>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={data?.results ?? []}
            pages={data?.count ? Math.ceil(data.count / params.page_size) : 0}
            params={params}
            setParams={setParams}
            showPagination={true}
            onRowClick={handleRowClick}
          />
        )}
      </Main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete') || 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_template_confirmation') || 'Are you sure you want to delete this template?'}{' '}
              {templateToDelete && (
                <span className="font-semibold">"{templateToDelete.title}"</span>
              )}
              {'. '}
              {t('action_cannot_be_undone') || 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
